/**
 * Email Campaigns Listing Page
 * View, manage, and monitor email campaigns
 *
 * Created: January 3, 2026
 */

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Badge, Card, ProgressBar } from "react-bootstrap";
import Modal from "react-modal";
import { toast } from "react-toastify";

import { selectUser } from "../../../components/auth/auth.selector";
import HelmetWrapper from "../../../HelmetWrapper";
import CmnFilters from "../../../components/lead-management/common/CmnFilters";
import ModalHeadAndClose from "../../../components/payouts-wtf/common/ModalHeadAndClose";
import ReactVortoxSpinner from "../../../components/common-components/ReactVortoxSpinner";
import PaymentLinkTable from "../../../components/payment-link-system/PaymentLinkTable";

import {
  fetchEmailCampaignsData,
  fetchEmailCampaignStatsData,
} from "../../../redux/email-broadcast/emailBroadcastSlice";
import {
  deleteEmailCampaign,
  duplicateCampaign,
  sendCampaignNow,
  pauseCampaign,
  resumeCampaign,
} from "../../../redux/email-broadcast/emailBroadcastApi";
import { formatDateTimezon } from "../../../components/common-data/Converts";

// Campaign Stats Modal Component
const CampaignStatsModal = ({ campaign, stats, loading, onClose }) => {
  if (!campaign) return null;

  const calculateRate = (count, total) => {
    if (!total) return "0%";
    return ((count / total) * 100).toFixed(1) + "%";
  };

  return (
    <div className="p-3">
      <h5 className="mb-3">{campaign.campaign_name}</h5>

      {loading ? (
        <div className="text-center py-5">
          <ReactVortoxSpinner />
        </div>
      ) : stats ? (
        <>
          {/* Progress */}
          <Card className="mb-3">
            <Card.Body>
              <h6 className="mb-3">Delivery Progress</h6>
              <ProgressBar
                now={(stats.sent_count / (stats.total_recipients || 1)) * 100}
                variant="success"
                className="mb-2"
                style={{ height: "20px" }}
              />
              <div className="d-flex justify-content-between">
                <span>Sent: {stats.sent_count?.toLocaleString()}</span>
                <span>Total: {stats.total_recipients?.toLocaleString()}</span>
              </div>
            </Card.Body>
          </Card>

          {/* Stats Grid */}
          <Row>
            <Col md={3}>
              <Card className="text-center mb-3">
                <Card.Body>
                  <h3 className="text-primary">{stats.sent_count?.toLocaleString() || 0}</h3>
                  <small className="text-muted">Delivered</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center mb-3">
                <Card.Body>
                  <h3 className="text-success">{stats.unique_open_count?.toLocaleString() || 0}</h3>
                  <small className="text-muted">
                    Opens ({calculateRate(stats.unique_open_count, stats.sent_count)})
                  </small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center mb-3">
                <Card.Body>
                  <h3 className="text-info">{stats.unique_click_count?.toLocaleString() || 0}</h3>
                  <small className="text-muted">
                    Clicks ({calculateRate(stats.unique_click_count, stats.sent_count)})
                  </small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center mb-3">
                <Card.Body>
                  <h3 className="text-danger">{stats.bounce_count?.toLocaleString() || 0}</h3>
                  <small className="text-muted">
                    Bounces ({calculateRate(stats.bounce_count, stats.sent_count)})
                  </small>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Detailed Stats */}
          <Row>
            <Col md={6}>
              <Card className="mb-3">
                <Card.Header>Engagement</Card.Header>
                <Card.Body className="p-0">
                  <table className="table table-sm mb-0">
                    <tbody>
                      <tr>
                        <td>Total Opens</td>
                        <td className="text-end">{stats.open_count?.toLocaleString() || 0}</td>
                      </tr>
                      <tr>
                        <td>Unique Opens</td>
                        <td className="text-end">{stats.unique_open_count?.toLocaleString() || 0}</td>
                      </tr>
                      <tr>
                        <td>Total Clicks</td>
                        <td className="text-end">{stats.click_count?.toLocaleString() || 0}</td>
                      </tr>
                      <tr>
                        <td>Unique Clicks</td>
                        <td className="text-end">{stats.unique_click_count?.toLocaleString() || 0}</td>
                      </tr>
                      <tr>
                        <td>Unsubscribes</td>
                        <td className="text-end">{stats.unsubscribe_count?.toLocaleString() || 0}</td>
                      </tr>
                    </tbody>
                  </table>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="mb-3">
                <Card.Header>Deliverability</Card.Header>
                <Card.Body className="p-0">
                  <table className="table table-sm mb-0">
                    <tbody>
                      <tr>
                        <td>Delivery Rate</td>
                        <td className="text-end">
                          {calculateRate(stats.sent_count - stats.bounce_count, stats.sent_count)}
                        </td>
                      </tr>
                      <tr>
                        <td>Bounce Rate</td>
                        <td className="text-end">{calculateRate(stats.bounce_count, stats.sent_count)}</td>
                      </tr>
                      <tr>
                        <td>Hard Bounces</td>
                        <td className="text-end">{stats.hard_bounce_count?.toLocaleString() || 0}</td>
                      </tr>
                      <tr>
                        <td>Soft Bounces</td>
                        <td className="text-end">{stats.soft_bounce_count?.toLocaleString() || 0}</td>
                      </tr>
                      <tr>
                        <td>Complaints</td>
                        <td className="text-end">{stats.complaint_count?.toLocaleString() || 0}</td>
                      </tr>
                    </tbody>
                  </table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Top Links */}
          {stats.top_links && stats.top_links.length > 0 && (
            <Card>
              <Card.Header>Top Clicked Links</Card.Header>
              <Card.Body className="p-0">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>URL</th>
                      <th className="text-end">Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.top_links.map((link, idx) => (
                      <tr key={idx}>
                        <td className="text-truncate" style={{ maxWidth: "400px" }}>
                          <a href={link.url} target="_blank" rel="noopener noreferrer">
                            {link.url}
                          </a>
                        </td>
                        <td className="text-end">{link.click_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card.Body>
            </Card>
          )}
        </>
      ) : (
        <p className="text-muted text-center">No stats available</p>
      )}

      <div className="mt-3">
        <button className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

const EmailCampaigns = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfoData = useSelector(selectUser);
  const { emailCampaigns, emailCampaignsLoading, campaignStats, campaignStatsLoading } = useSelector(
    (state) => state.emailBroadcast || {}
  );

  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(20);

  const iniAllFilter = { searchString: "", status: "" };
  const [allFilters, setAllFilters] = useState(iniAllFilter);
  const [filters, setFilters] = useState({ page: 1, limit: 20 });
  const [searchFilterApplied, setSearchFilterApplied] = useState(false);

  const iniModValues = { isModOpen: false, modName: "", rowData: null };
  const [modValues, setModValues] = useState(iniModValues);
  const [selectedStatus, setSelectedStatus] = useState("all");

  const fetchCampaigns = (filters) => {
    dispatch(fetchEmailCampaignsData({ filters, token: userInfoData?.token }));
  };

  useEffect(() => {
    fetchCampaigns(filters);
  }, [filters]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, page: isPage + 1, limit: pageLimit }));
  }, [isPage, pageLimit]);

  useEffect(() => {
    if (selectedStatus !== "all") {
      setFilters((prev) => ({ ...prev, status: selectedStatus, page: 1 }));
    } else {
      const { status, ...rest } = filters;
      setFilters({ ...rest, page: 1 });
    }
  }, [selectedStatus]);

  const onClickSearch = () => {
    setIsPage(0);
    if (allFilters.searchString) {
      setSearchFilterApplied(true);
      setFilters((prev) => ({ ...prev, search: allFilters.searchString }));
    }
  };

  const onResetFilters = () => {
    setAllFilters(iniAllFilter);
    setSelectedStatus("all");
    setFilters({ page: 1, limit: 20 });
  };

  const handleViewStats = (campaign) => {
    dispatch(fetchEmailCampaignStatsData({ id: campaign.uid, token: userInfoData?.token }));
    setModValues({ isModOpen: true, modName: "stats", rowData: campaign });
  };

  const handleDelete = async (campaign) => {
    if (campaign.campaign_status === "sending") {
      toast.error("Cannot delete a campaign that is currently sending");
      return;
    }
    if (!window.confirm(`Delete campaign "${campaign.campaign_name}"?`)) return;

    try {
      const response = await deleteEmailCampaign(campaign.uid, userInfoData?.token);
      if (response?.status) {
        toast.success("Campaign deleted");
        fetchCampaigns(filters);
      } else {
        toast.error(response?.message || "Delete failed");
      }
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  const handleDuplicate = async (campaign) => {
    try {
      const response = await duplicateCampaign(campaign.uid, userInfoData?.token);
      if (response?.status) {
        toast.success("Campaign duplicated");
        fetchCampaigns(filters);
      } else {
        toast.error(response?.message || "Duplicate failed");
      }
    } catch (error) {
      toast.error("Duplicate failed");
    }
  };

  const handleSendNow = async (campaign) => {
    if (!window.confirm(`Send campaign "${campaign.campaign_name}" now?`)) return;

    try {
      const response = await sendCampaignNow(campaign.uid, userInfoData?.token);
      if (response?.status) {
        toast.success("Campaign is being sent");
        fetchCampaigns(filters);
      } else {
        toast.error(response?.message || "Send failed");
      }
    } catch (error) {
      toast.error("Send failed");
    }
  };

  const handlePause = async (campaign) => {
    try {
      const response = await pauseCampaign(campaign.uid, userInfoData?.token);
      if (response?.status) {
        toast.success("Campaign paused");
        fetchCampaigns(filters);
      } else {
        toast.error(response?.message || "Pause failed");
      }
    } catch (error) {
      toast.error("Pause failed");
    }
  };

  const handleResume = async (campaign) => {
    try {
      const response = await resumeCampaign(campaign.uid, userInfoData?.token);
      if (response?.status) {
        toast.success("Campaign resumed");
        fetchCampaigns(filters);
      } else {
        toast.error(response?.message || "Resume failed");
      }
    } catch (error) {
      toast.error("Resume failed");
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: "secondary",
      scheduled: "info",
      sending: "warning",
      paused: "dark",
      sent: "success",
      failed: "danger",
      cancelled: "secondary",
    };
    return <Badge bg={colors[status] || "secondary"}>{status}</Badge>;
  };

  const tbColumns = [
    {
      dataField: "campaign_name",
      text: "Campaign",
      formatter: (_, row) => (
        <div>
          <span
            className="fw-medium text-primary"
            style={{ cursor: "pointer" }}
            onClick={() => navigate(`/broadcast-system/email-broadcast/campaigns/${row.uid}`)}
          >
            {row.campaign_name}
          </span>
          <small className="d-block text-muted">{row.subject}</small>
        </div>
      ),
    },
    {
      dataField: "campaign_status",
      text: "Status",
      formatter: (_, row) => (
        <div>
          {getStatusBadge(row.campaign_status)}
          {row.campaign_status === "sending" && (
            <small className="d-block text-muted mt-1">
              {row.sent_count || 0}/{row.total_recipients || 0}
            </small>
          )}
        </div>
      ),
    },
    {
      dataField: "list_name",
      text: "List",
      formatter: (_, row) => row.list_name || "-",
    },
    {
      dataField: "sent_count",
      text: "Sent",
      formatter: (_, row) => row.sent_count?.toLocaleString() || 0,
    },
    {
      dataField: "unique_open_count",
      text: "Opens",
      formatter: (_, row) => (
        <span>
          {row.unique_open_count || 0}
          {row.sent_count > 0 && (
            <small className="text-muted ms-1">
              ({((row.unique_open_count / row.sent_count) * 100).toFixed(1)}%)
            </small>
          )}
        </span>
      ),
    },
    {
      dataField: "unique_click_count",
      text: "Clicks",
      formatter: (_, row) => (
        <span>
          {row.unique_click_count || 0}
          {row.sent_count > 0 && (
            <small className="text-muted ms-1">
              ({((row.unique_click_count / row.sent_count) * 100).toFixed(1)}%)
            </small>
          )}
        </span>
      ),
    },
    {
      dataField: "date_added",
      text: "Created",
      formatter: (_, row) => <small>{formatDateTimezon(row.date_added) || "N/A"}</small>,
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) => (
        <div className="d-flex gap-1 flex-wrap">
          <button
            className="btn btn-sm btn-outline-info"
            onClick={(e) => {
              e.stopPropagation();
              handleViewStats(row);
            }}
          >
            Stats
          </button>

          {row.campaign_status === "draft" && (
            <>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/broadcast-system/email-broadcast/campaigns/${row.uid}/edit`);
                }}
              >
                Edit
              </button>
              <button
                className="btn btn-sm btn-success"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSendNow(row);
                }}
              >
                Send
              </button>
            </>
          )}

          {row.campaign_status === "sending" && (
            <button
              className="btn btn-sm btn-warning"
              onClick={(e) => {
                e.stopPropagation();
                handlePause(row);
              }}
            >
              Pause
            </button>
          )}

          {row.campaign_status === "paused" && (
            <button
              className="btn btn-sm btn-success"
              onClick={(e) => {
                e.stopPropagation();
                handleResume(row);
              }}
            >
              Resume
            </button>
          )}

          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={(e) => {
              e.stopPropagation();
              handleDuplicate(row);
            }}
          >
            Copy
          </button>

          {row.campaign_status !== "sending" && (
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row);
              }}
            >
              Delete
            </button>
          )}
        </div>
      ),
    },
  ];

  // Calculate status counts
  const statusCounts = {
    all: emailCampaigns?.pagination?.totalRecords || 0,
    draft: emailCampaigns?.data?.filter((c) => c.campaign_status === "draft").length || 0,
    scheduled: emailCampaigns?.data?.filter((c) => c.campaign_status === "scheduled").length || 0,
    sending: emailCampaigns?.data?.filter((c) => c.campaign_status === "sending").length || 0,
    sent: emailCampaigns?.data?.filter((c) => c.campaign_status === "sent").length || 0,
  };

  return (
    <HelmetWrapper title="WTF: Email Campaigns">
      <Container className="bg-cloud-k px-50px gobal-container" fluid>
        {/* Stats Modal */}
        <Modal
          isOpen={modValues.isModOpen && modValues.modName === "stats"}
          onRequestClose={() => setModValues(iniModValues)}
          contentLabel="Campaign Stats"
          ariaHideApp={false}
          style={{
            content: {
              maxWidth: "800px",
              margin: "auto",
              maxHeight: "90vh",
              overflow: "auto",
            },
          }}
        >
          <div className="createNewCTOModalContainer">
            <ModalHeadAndClose
              title="Campaign Stats"
              onClose={() => setModValues(iniModValues)}
            />
            <CampaignStatsModal
              campaign={modValues.rowData}
              stats={campaignStats}
              loading={campaignStatsLoading}
              onClose={() => setModValues(iniModValues)}
            />
          </div>
        </Modal>

        {/* Header */}
        <Row className="mb-3">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1">Email Campaigns</h5>
                <small className="text-muted">
                  {emailCampaigns?.pagination?.totalRecords || 0} campaigns
                </small>
              </div>
              <button
                className="btn btn-primary"
                style={{ backgroundColor: "#D2000B", borderColor: "#D2000B" }}
                onClick={() => navigate("/broadcast-system/email-broadcast/campaigns/new")}
              >
                + Create Campaign
              </button>
            </div>
          </Col>
        </Row>

        {/* Status Tabs */}
        <Row className="mb-3">
          <Col>
            <Card>
              <Card.Body className="py-2">
                <div className="d-flex gap-3">
                  {[
                    { key: "all", label: "All" },
                    { key: "draft", label: "Drafts" },
                    { key: "scheduled", label: "Scheduled" },
                    { key: "sending", label: "Sending" },
                    { key: "sent", label: "Sent" },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      className={`btn btn-sm ${selectedStatus === tab.key ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setSelectedStatus(tab.key)}
                      style={selectedStatus === tab.key ? { backgroundColor: "#D2000B", borderColor: "#D2000B" } : {}}
                    >
                      {tab.label}
                      <Badge
                        bg={selectedStatus === tab.key ? "light" : "secondary"}
                        text={selectedStatus === tab.key ? "dark" : "white"}
                        className="ms-1"
                      >
                        {statusCounts[tab.key]}
                      </Badge>
                    </button>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col>
            <CmnFilters
              from="email-campaigns"
              userInfoData={userInfoData}
              searchFilterApplied={searchFilterApplied}
              filters={allFilters}
              setFilters={setAllFilters}
              onClickSearch={onClickSearch}
              onClickReset={onResetFilters}
              isLoading={emailCampaignsLoading}
              searchPlaceHolder="Search campaigns..."
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table mt15">
              {emailCampaignsLoading ? (
                <ReactVortoxSpinner />
              ) : (
                <PaymentLinkTable
                  tableData={emailCampaigns?.data}
                  tableColumns={tbColumns}
                  tablePagination={emailCampaigns?.pagination}
                  isPage={isPage}
                  setIsPage={setIsPage}
                  pageLimit={pageLimit}
                  setPageLimit={setPageLimit}
                />
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </HelmetWrapper>
  );
};

export default EmailCampaigns;
